import React, { useState, useEffect } from 'react';
import { Map, Bin, Plus, InfoCircle, Check, CheckCircle, WarningCircle } from 'iconoir-react';
import { Modal, Button, Alert, Card, Form, Tab, Tabs } from 'react-bootstrap';
import BuscaEndereco from './BuscaEndereco';
import { useCarrinho } from '../services/CarrinhoContext';
import { getClienteProfile, adicionarEndereco, removerEndereco } from '../services/api';
import './ModalMobile.css';

function EnderecoSelectorModal({ show, onHide, brand }) {
  const { setEndereco, endereco: enderecoAtual } = useCarrinho();
  const [activeTab, setActiveTab] = useState('salvos');
  const [enderecosSalvos, setEnderecosSalvos] = useState([]);
  const [enderecoSelecionado, setEnderecoSelecionado] = useState(null);
  const [novoEndereco, setNovoEndereco] = useState(null);
  const [complemento, setComplemento] = useState('');
  const [referencia, setReferencia] = useState('');
  const [nomeEntrega, setNomeEntrega] = useState('');
  const [telefoneEntrega, setTelefoneEntrega] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Carregar endereços salvos e dados do usuário
  useEffect(() => {
    if (show) {
      carregarEnderecosSalvos();
      preencherDadosUsuario();
    } else {
      resetForm();
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const carregarEnderecosSalvos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('sitri_token');
      if (token) {
        const resultado = await getClienteProfile(token);
        if (resultado && resultado.success) {
          const enderecos = resultado.enderecos || [];
          setEnderecosSalvos(enderecos);
          // Se não há endereços salvos, ir direto para aba "novo"
          if (enderecos.length === 0) {
            setActiveTab('novo');
          } else if (enderecoAtual) {
            // Pré-selecionar o endereço que já está em uso no contexto
            const enderecoEmUso = enderecos.find(e => {
              const enderecoFormatado = `${e.rua}, ${e.numero} - ${e.bairro}, ${e.cidade}`;
              return enderecoFormatado === enderecoAtual.endereco ||
                (e.lat && enderecoAtual.lat && e.lat === enderecoAtual.lat && e.lng === enderecoAtual.lng);
            });
            if (enderecoEmUso) {
              setEnderecoSelecionado(enderecoEmUso);
            }
          }
        }
      }
    } catch (error) {
      setActiveTab('novo');
    } finally {
      setLoading(false);
    }
  };

  const preencherDadosUsuario = () => {
    const user = localStorage.getItem('sitri_user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        setNomeEntrega(userData.nome || '');
        setTelefoneEntrega(userData.telefone || '');
      } catch (error) {
      }
    }
  };

  const temTelefoneUsuario = () => {
    const user = localStorage.getItem('sitri_user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        return userData.telefone && userData.telefone.trim().length > 0;
      } catch (error) {
        return false;
      }
    }
    return false;
  };

  const resetForm = () => {
    setActiveTab('salvos');
    setEnderecoSelecionado(null);
    setNovoEndereco(null);
    setComplemento('');
    setReferencia('');
    setError('');
  };

  const handleSelecionarEnderecoSalvo = async (endereco) => {
    if (!temTelefoneUsuario() && !telefoneEntrega.trim()) {
      setError('Telefone é obrigatório para entrega');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const enderecoFormatado = `${endereco.rua}, ${endereco.numero} - ${endereco.bairro}, ${endereco.cidade}`;
      
      // Validar endereço para obter taxa de entrega atualizada
      const { validarEnderecoEntrega } = await import('../services/api');
      const validacao = await validarEnderecoEntrega(
        brand,
        enderecoFormatado,
        endereco.cep,
        endereco.lat,
        endereco.lng
      );
      
      if (validacao && validacao.success && validacao.entrega) {
        const enderecoCompleto = {
          endereco: enderecoFormatado,
          lat: endereco.lat,
          lng: endereco.lng,
          place_id: endereco.place_id,
          complemento: endereco.complemento || '',
          entrega: {
            ...validacao.entrega,
            nome: nomeEntrega.trim() || null,
            telefone: telefoneEntrega.trim() || null
          }
        };
        
        setEndereco(enderecoCompleto);
        onHide();
      } else {
        setError(validacao?.message || 'Erro ao validar endereço');
      }
    } catch (error) {
      setError('Erro ao validar endereço');
    } finally {
      setLoading(false);
    }
  };

  const handleNovoEnderecoSelected = (endereco) => {
    setNovoEndereco(endereco);
    setError('');
  };

  const handleSalvarNovoEndereco = async () => {
    if (!novoEndereco) {
      setError('Selecione um endereço');
      return;
    }

    if (!temTelefoneUsuario() && !telefoneEntrega.trim()) {
      setError('Telefone é obrigatório para entrega');
      return;
    }

    const token = localStorage.getItem('sitri_token');
    if (!token) {
      setError('É necessário estar logado para salvar endereços');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const dadosEndereco = {
        endereco: novoEndereco.endereco,
        lat: novoEndereco.lat,
        lng: novoEndereco.lng,
        place_id: novoEndereco.place_id,
        complemento: complemento.trim(),
        referencia: referencia.trim()
      };

      const resultado = await adicionarEndereco(token, dadosEndereco);
      
      if (resultado && resultado.success) {
        // Usar o endereço recém salvo
        const enderecoCompleto = {
          endereco: novoEndereco.endereco,
          lat: novoEndereco.lat,
          lng: novoEndereco.lng,
          place_id: novoEndereco.place_id,
          complemento: complemento,
          entrega: {
            ...novoEndereco.entrega,
            nome: nomeEntrega.trim() || null,
            telefone: telefoneEntrega.trim() || null
          }
        };
        
        setEndereco(enderecoCompleto);
        onHide();
      } else {
        setError(resultado?.message || 'Erro ao salvar endereço');
      }
    } catch (error) {
      setError('Erro ao salvar endereço');
    } finally {
      setLoading(false);
    }
  };

  const handleUsarNovoEndereco = () => {
    if (!temTelefoneUsuario() && !telefoneEntrega.trim()) {
      setError('Telefone é obrigatório para entrega');
      return;
    }

    if (novoEndereco) {
      const enderecoCompleto = {
        endereco: novoEndereco.endereco,
        lat: novoEndereco.lat,
        lng: novoEndereco.lng,
        place_id: novoEndereco.place_id,
        complemento: complemento,
        entrega: {
          ...novoEndereco.entrega,
          nome: nomeEntrega.trim() || null,
          telefone: telefoneEntrega.trim() || null
        }
      };
      
      setEndereco(enderecoCompleto);
      onHide();
    }
  };

  const handleRemoverEndereco = async (endereco) => {
    try {
      const token = localStorage.getItem('sitri_token');
      const resultado = await removerEndereco(token, endereco.id);
      if (resultado && resultado.success) {
        await carregarEnderecosSalvos();
      } else {
        setError('Erro ao remover endereço');
      }
    } catch (error) {
      setError('Erro ao remover endereço');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{borderBottom:'1px solid #e5e7eb'}}>
        <Modal.Title style={{fontWeight:700, fontSize:'1.1rem', color:'#111827'}}>Selecionar Endereço de Entrega</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{backgroundColor:'#f9fafb', padding:'20px'}}>
        {error && (
          <Alert variant="danger" className="mb-3" style={{borderRadius:'12px', fontSize:'0.85rem', padding:'12px'}}>
            <small>{error}</small>
          </Alert>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab eventKey="salvos" title="Meus Endereços">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
              </div>
            ) : enderecosSalvos.length > 0 ? (
              <div>
                {enderecosSalvos.map((endereco, index) => (
                  <div
                    key={index}
                    onClick={() => setEnderecoSelecionado(endereco)}
                    className="d-flex align-items-center p-3"
                    style={{
                      background: '#fff',
                      border: `2px solid ${enderecoSelecionado?.id === endereco.id ? '#0039ff' : '#e5e7eb'}`,
                      borderRadius: '12px',
                      boxShadow: enderecoSelecionado?.id === endereco.id ? '0 4px 12px rgba(0,57,255,0.15)' : '0 1px 2px rgba(0,0,0,0.04)',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Form.Check
                      type="radio"
                      name="enderecoSelecionado"
                      id={`endereco-${endereco.id}`}
                      className="me-3"
                      checked={enderecoSelecionado?.id === endereco.id}
                      onChange={() => setEnderecoSelecionado(endereco)}
                      style={{marginTop:'2px'}}
                    />
                    <div className="d-flex align-items-center flex-grow-1" style={{gap: '12px'}}>
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center">
                          <span className="fw-bold" style={{fontSize:'0.9rem', color:'#111827'}}>{endereco.rua}, {endereco.numero}</span>
                        </div>
                        <div style={{color:'#6b7280', fontSize:'0.8rem'}}>
                          {endereco.bairro}, {endereco.cidade}{endereco.cep && ` • CEP: ${endereco.cep}`}
                        </div>
                        {endereco.complemento && (
                          <div style={{color:'#6b7280', fontSize:'0.75rem', marginTop:'4px'}}>
                            {endereco.complemento}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleRemoverEndereco(endereco); }}
                      style={{minWidth:'auto'}}
                    >
                      <Bin width="16" height="16" />
                    </Button>
                  </div>
                ))}
                {!temTelefoneUsuario() && (
                  <div className="mb-3">
                    <Form.Group>
                      <Form.Label className="small">
                        Telefone para Contato <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        value={telefoneEntrega}
                        onChange={(e) => setTelefoneEntrega(e.target.value.replace(/\D/g, ''))}
                        placeholder="Telefone obrigatório"
                        size="sm"
                        required
                        isInvalid={!telefoneEntrega.trim() && error.includes('Telefone')}
                        maxLength={11}
                      />
                      <Form.Text className="text-muted">
                        Obrigatório para contato durante a entrega
                      </Form.Text>
                    </Form.Group>
                  </div>
                )}
                
                <div className="mt-3">
                  <Button
                    variant="primary"
                    onClick={() => handleSelecionarEnderecoSalvo(enderecoSelecionado)}
                    disabled={!enderecoSelecionado || (!temTelefoneUsuario() && !telefoneEntrega.trim())}
                    className="w-100 btn-primary-glow"
                    style={{
                      padding: '12px',
                      borderRadius: '12px',
                      backgroundColor: '#0039ff',
                      border: 'none',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(0,106,255,0.25)'
                    }}
                  >
                    <Check className="me-2" />
                    Usar Endereço Selecionado
                  </Button>
                </div>
                
              </div>
            ) : (
              <div className="text-center py-4">
                <span className="text-muted mb-3" style={{fontSize:'2rem'}}><Map /></span>
                <p className="text-muted">Nenhum endereço salvo</p>
              </div>
            )}
          </Tab>

          <Tab eventKey="novo" title="Adicionar Endereço">
            <div className="mb-3">
              <label className="form-label" style={{fontSize:'0.85rem', color:'#6b7280', marginBottom:'8px', fontWeight:600}}>Buscar Endereço</label>
              <BuscaEndereco 
                onSelected={handleNovoEnderecoSelected}
                brand={brand}
              />
            </div>

            {novoEndereco && (
              <>
                <div className="mb-3 p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
                  <div className="d-flex align-items-start" style={{gap:'12px'}}>
                    <div style={{width:'36px', height:'36px', borderRadius:'8px', background:'#f9fafb', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', flexShrink:0}}>
                      <Map style={{color:'#6b7280'}} width="20" height="20" />
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:'0.85rem', color:'#111827', fontWeight:500}}>{novoEndereco.endereco}</div>
                    </div>
                  </div>
                </div>

                {novoEndereco.entrega && novoEndereco.entrega.podeEntregar && (
                  <Alert 
                    variant="success"
                    className="mb-3"
                    style={{borderRadius:'12px', fontSize:'0.85rem', padding:'12px'}}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" />
                      <strong>Endereço dentro da área de entrega!</strong>
                    </div>
                    <div style={{ fontSize: '12px' }}>
                      Valor: <strong>R$ {novoEndereco.entrega.valorEntrega?.toFixed(2) || '0,00'}</strong>
                      {novoEndereco.entrega.tempoEntrega && (
                        <>
                          {' • '} Tempo: <strong>{novoEndereco.entrega.tempoEntrega} min</strong>
                        </>
                      )}
                    </div>
                  </Alert>
                )}

                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label style={{fontSize:'0.85rem', color:'#6b7280', marginBottom:'8px', fontWeight:600}}>Complemento</Form.Label>
                      <Form.Control
                        type="text"
                        value={complemento}
                        onChange={(e) => setComplemento(e.target.value)}
                        placeholder="Ex: Apto 3B do bloco J."
                        style={{
                          fontSize:'0.85rem',
                          padding:'10px 12px',
                          borderRadius:'12px',
                          border:'1px solid #e5e7eb',
                          backgroundColor:'#fff'
                        }}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label style={{fontSize:'0.85rem', color:'#6b7280', marginBottom:'8px', fontWeight:600}}>Ponto de Referência</Form.Label>
                      <Form.Control
                        type="text"
                        value={referencia}
                        onChange={(e) => setReferencia(e.target.value)}
                        placeholder="Ex: Aos fundos do mercado, casa vermelha."
                        style={{
                          fontSize:'0.85rem',
                          padding:'10px 12px',
                          borderRadius:'12px',
                          border:'1px solid #e5e7eb',
                          backgroundColor:'#fff'
                        }}
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label style={{fontSize:'0.85rem', color:'#6b7280', marginBottom:'8px', fontWeight:600}}>Nome para Entrega</Form.Label>
                      <Form.Control
                        type="text"
                        value={nomeEntrega}
                        onChange={(e) => setNomeEntrega(e.target.value)}
                        placeholder="Quem vai receber"
                        style={{
                          fontSize:'0.85rem',
                          padding:'10px 12px',
                          borderRadius:'12px',
                          border:'1px solid #e5e7eb',
                          backgroundColor:'#fff'
                        }}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label style={{fontSize:'0.85rem', color:'#6b7280', marginBottom:'8px', fontWeight:600}}>
                        Telefone para Contato
                        {!temTelefoneUsuario() && <span className="text-danger"> *</span>}
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        value={telefoneEntrega}
                        onChange={(e) => setTelefoneEntrega(e.target.value.replace(/\D/g, ''))}
                        placeholder={!temTelefoneUsuario() ? "Telefone obrigatório" : "Telefone para contato"}
                        required={!temTelefoneUsuario()}
                        isInvalid={!temTelefoneUsuario() && !telefoneEntrega.trim() && error.includes('Telefone')}
                        maxLength={11}
                        style={{
                          fontSize:'0.85rem',
                          padding:'10px 12px',
                          borderRadius:'12px',
                          border:'1px solid #e5e7eb',
                          backgroundColor:'#fff'
                        }}
                      />
                      {!temTelefoneUsuario() && (
                        <Form.Text className="text-muted">
                          Obrigatório para contato durante a entrega
                        </Form.Text>
                      )}
                    </Form.Group>
                  </div>
                </div>

                {novoEndereco.entrega && !novoEndereco.entrega.podeEntregar && (
                  <Alert 
                    variant="warning"
                    className="mb-3"
                    style={{borderRadius:'12px', fontSize:'0.85rem', padding:'12px'}}
                  >
                    <div className="d-flex align-items-center">
                      <WarningCircle className="me-2" />
                      <strong>Endereço fora da área de entrega!</strong>
                    </div>
                  </Alert>
                )}

                <div className="d-flex gap-2">
                  <Button 
                    variant="primary" 
                    onClick={handleSalvarNovoEndereco}
                    disabled={loading || !novoEndereco}
                    className="w-100 btn-primary-glow"
                    style={{
                      padding: '12px',
                      borderRadius: '12px',
                      backgroundColor: loading ? '#0056cc' : '#0039ff',
                      border: 'none',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(0,106,255,0.25)'
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Check className="me-2" />
                        Salvar e Usar Endereço
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>
      
    </Modal>
  );
}

export default EnderecoSelectorModal;
